"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const config_1 = require("../config");
const authentication_service_g_1 = require("./authentication-service.g");
const host_cache_1 = require("../host-cache");
const service_requester_1 = require("../service-requester");
/**
 * Used to authenticate with the cloud-link platform and maintain access to access and refresh tokens
 * @class  Authentication
*/
class AuthenticationService extends authentication_service_g_1.AuthenticationService {
    /**
     * Constructor with parameters to support various environments. You can manage your own default persistance layer for token management by passing in an implementation of the StorageAsync interface. You can provide your own http implementation see here https://github.com/qubyte/fetch-ponyfill.
     * @constructor
     * @param {string} params.version For targeting a specific version
     * @param {string} params.cloud For targeting a specific cloud
     * @param {string} params.stage For targeting a specific stage
     * @param {string} params.url For targeting a specific url.  Overrides version, cloud, and stage.
     * @param {string} params.http Supply custom http instance
     * @param {string} params.store Supply custom store instance
     */
    constructor(params = {}) {
        params.http = params.http || config_1.Config.http;
        params.hostCache = params.hostCache || new host_cache_1.HostCache({
            http: params.http,
        });
        params.serviceRequester = params.serviceRequester || new service_requester_1.ServiceRequester(Object.assign({ service: 'Authentication', noCacheFetch: true }, params));
        super(params);
        this.store = params.store || config_1.Config.storage;
    }
    /**
     * Login with the flow matching the parameters provided
     * user
     * @param {string} params.user.username - username
     * @param {string} params.user.password - password
     * @param {string} params.user.accountId - accountId. If your userId includes a top level domain, like darren@mitel.com, mitel.com can be matched as an accountId on the system if registered client
     * @param {string} params.client.clientId - client id
     * @param {string} params.client.clientSecret - client secret
     * @param {string} params.client.accountId - cloudlink account id code
     * @param {string} params.code.code - Authorization Code
     * @param {string} params.code.clientId - client id for application
     * @param {string} params.code.redirect_uri -redirect_uri for application
     * @param {string} params.code.scope = scope
     */
    login(params) {
        if (params.user) {
            return this.createToken(Object.assign({ grantType: 'password' }, params.user))
                .then(token => this.setToken(token));
        }
        else if (params.client) {
            return this.createToken(Object.assign({ grantType: 'client_credentials' }, params.client))
                .then(token => this.setToken(token));
        }
        else if (params.code) {
            return this.createToken(Object.assign({ grantType: 'authorization_code' }, params.code))
                .then(token => this.setToken(token));
        }
        else {
            return Promise.reject(new Error('No credentials supplied'));
        }
    }
    /**
     * Login as a guest.  Account ID must be provided.  Account must have the allowGuestAccess policy.
     *
     * @param {string} params.principalId - principal identity for access to cloudlink services
     * @param {string} params.accountId - account ID.
     * @param {string} params.name - The name to be provided to CloudLink services.
     */
    guestLogin(params) {
        return this.createGuestToken(params)
            .then(token => this.setToken(token));
    }
    /**
     * Logout clears your local persistance cache
     */
    logout(_args = {}) {
        return Promise.all([
            this.removeItem('access_token'),
            this.removeItem('refresh_token'),
            this.removeItem('token_type'),
            this.removeItem('original_token'),
            this.removeItem(''),
        ]).then(_ => { });
    }
    /**
     * Refresh will update your access tokens based on a refresh token.  It will use the given token or attempt to get a cached token if not was given.  Some applications may want to manage tokens externally and therefore can use the optional argument.
     * @param {Token} token - Optional token used if managing your own state. Otherwise the local cache is used.
     */
    refresh(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const claimsToken = yield this.getToken(token);
            if (!claimsToken) {
                throw new Error('No token passed and no token could be found in localstorage. Please login and / or pass a valid token');
            }
            else {
                try {
                    const token = yield this.createToken({
                        grantType: 'refresh_token',
                        refreshToken: claimsToken.refresh_token,
                        scope: claimsToken.scope
                    });
                    yield this.setToken(token);
                    return token;
                }
                catch (error) {
                    if (error.statusCode === 401 || error.statusCode === 403) {
                        yield this.logout();
                    }
                    throw error;
                }
            }
        });
    }
    /**
     * Retreive your current claims based on your existing token. Your claims represent the data that you retrived when you first received this token. It may not represent your current profile if there have been changes since receipt of this token.
     * If you want to update your claims then you need to authenticate. This can be done by calling refresh or re-logging in if a refresh token isn't available.
     * If an error is retreived it will try to update your access tokens based on a refresh token. It will use it's cached tokens if available unless handed in. Some
     * applications may want to manage tokens externally and therefore can use the optional argument.
     * @param {Token} token - Optional token used if managing your own state. Otherwise the local cache is used.
     */
    whoAmI(token) {
        return this.getToken(token)
            .then(claimsToken => this.fetchClaims(claimsToken))
            .catch(error => {
            if (error.statusCode === 401) {
                return this.refresh(token)
                    .then(updatedToken => this.fetchClaims(updatedToken));
            }
            else {
                throw error;
            }
        });
    }
    /**
     * Request an assumed role token, then stores the token
     * @param {Token} token
     * @param {AssumeRoleTokenPostRequest} body - information for assumed role
     */
    assumeRole(body, token) {
        return this.getToken(token)
            .then(orgToken => this.fetchNewRole(orgToken, body))
            .catch(error => {
            if (error.statusCode === 401) {
                return this.refresh(token)
                    .then(updatedToken => this.fetchNewRole(updatedToken, body));
            }
            else {
                throw error;
            }
        });
    }
    /**
     * Sets the original token back to @mitel/token in storage
     */
    revertRole() {
        if (this.store) {
            return this.store.getItem(AuthenticationService.tokenKey + '.' + 'original_token').then(data => {
                if (data) {
                    this.removeItem('original_token');
                    return this.setToken(JSON.parse(data));
                }
                else {
                    return Promise.reject(new Error("Original token not found"));
                }
            });
        }
        else {
            return Promise.reject(new Error("No storage provider is set. You must specify a storage provider to hand in the token yourself"));
        }
    }
    /**
     * Sets the token used for requests.
     * @param token
     */
    setToken(token) {
        return Promise.all([
            this.setItem('access_token', token.access_token),
            this.setItem('refresh_token', token.refresh_token),
            this.setItem('token_type', token.token_type),
            this.setItem('', JSON.stringify(token)),
        ]).then(_ => token);
    }
    /**
     * Retrieves the currently stored token.
     * @param token
     */
    getToken(token) {
        return __awaiter(this, void 0, void 0, function* () {
            if (token) {
                return token;
            }
            else if (this.store) {
                const data = yield this.store.getItem(AuthenticationService.tokenKey);
                return data && JSON.parse(data);
            }
            else {
                throw new Error("No storage provider is set. You must specify a storage provider to hand in the token yourself");
            }
        });
    }
    /**
     * Indicates if there's a stored token.
     */
    hasToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.getToken();
            return !!token;
        });
    }
    /**
     * Tests a policy condition against applied policy to the current user or a specified user if allowed
     * @param request The Policy Test Request Object
     */
    testPolicy(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = (yield this.getToken()) || { access_token: '' }; // we want the call to go ahead to the server so the client gets a normal http error i.e. 401/403 vs throwing on our own here
            return this.evaluatePolicy({ body: request, options: this.getAuthOptions(token) });
        });
    }
    /**
     * Retrieves the given SAML identity provider from the account in the claims
     * @param {Token} token
     * @param {string} identityProviderId - The ID of the identity provider to retrieve, in most cases this is the account ID
     */
    getSamlIdentityProvider(identityProviderId, token) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.executeWithRetry((tokenToUse) => __awaiter(this, void 0, void 0, function* () {
                return yield this.getIdentityProvider({ identityProviderId, options: this.getAuthOptions(tokenToUse) });
            }), token);
        });
    }
    /**
     * Retrieves the given SAML identity providers from the account in the claims
     * @param {Token} token
     */
    getSamlIdentityProviders(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.executeWithRetry((tokenToUse) => __awaiter(this, void 0, void 0, function* () {
                return yield this.getIdentityProviders({ options: this.getAuthOptions(tokenToUse) });
            }), token);
        });
    }
    /**
     * Creates a SAML Identity Provider
     * @param {Token} token
     * @param {IdentityProviderPostRequest} request - The Idenity Provider object to create
     */
    createSamlIdentityProvider(request, token) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.executeWithRetry((tokenToUse) => __awaiter(this, void 0, void 0, function* () {
                return yield this.createIdentityProvider({ body: request, options: this.getAuthOptions(tokenToUse) });
            }), token);
        });
    }
    /**
     * Updates a SAML Identity Provider
     * @param {Token} token
     * @param {string} identityProviderId - The ID of the Identity Provider to update
     * @param {IdentityProviderPutRequest} request - The Idenity Provider object to update
     */
    updateSamlIdentityProvider(identityProviderId, request, token) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.executeWithRetry((tokenToUse) => __awaiter(this, void 0, void 0, function* () {
                return yield this.updateIdentityProvider({ identityProviderId, body: request, options: this.getAuthOptions(tokenToUse) });
            }), token);
        });
    }
    /**
     * Delete a SAML Identity Provider
     * @param {Token} token
     * @param {string} identityProviderId - The ID of the identity provider to delete
     */
    deleteSamlIdentityProvider(identityProviderId, token) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.executeWithRetry((tokenToUse) => __awaiter(this, void 0, void 0, function* () {
                return yield this.deleteIdentityProvider({ identityProviderId, options: this.getAuthOptions(tokenToUse) });
            }), token);
        });
    }
    /**
     * A helper method to call a function and retry the function in case of failure due to 401 unauthorized
     * @param funcToCall - the function to call
     * @param token - the optional existing token to use
     * @returns T - the generic type
     */
    executeWithRetry(funcToCall, token) {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            const orgToken = yield this.getToken(token);
            try {
                response = yield funcToCall(orgToken);
            }
            catch (error) {
                if (error.statusCode === 401) {
                    const newToken = yield this.refresh(token);
                    return yield funcToCall(newToken);
                }
                else {
                    throw error;
                }
            }
            return response;
        });
    }
    removeItem(key) {
        if (this.store) {
            return this.store.removeItem(`${AuthenticationService.tokenKey}${key ? '.' + key : ''}`);
        }
        else {
            return Promise.resolve();
        }
    }
    getAuthOptions(token) {
        const options = {
            headers: {
                'content-type': 'application/json',
                authorization: `Bearer ${token.access_token}`
            }
        };
        return options;
    }
    fetchClaims(token) {
        return this.getClaims({
            options: this.getAuthOptions(token)
        });
    }
    fetchNewRole(token, body) {
        return this.createTokenWithAssumedRole({
            body,
            options: this.getAuthOptions(token)
        }).then(token => this.setAssumeRoleToken(token));
    }
    setAssumeRoleToken(token) {
        // Save the original token before replacing token
        if (this.store) {
            return this.store.getItem(AuthenticationService.tokenKey)
                .then(data => data && this.setItem('original_token', data))
                .then(() => this.setToken(token));
        }
        else {
            return this.setToken(token);
        }
    }
    setItem(key, value) {
        if (this.store) {
            return this.store.setItem(this.getStoreKeyFor(key), value);
        }
        else {
            return Promise.resolve();
        }
    }
    getStoreKeyFor(key) {
        return `${AuthenticationService.tokenKey}${key ? '.' + key : ''}`;
    }
}
exports.AuthenticationService = AuthenticationService;
AuthenticationService.tokenKey = '@mitel/token';
